import { icon } from "./iconsKit";
import { btnHtml, ribbonHtml } from "./kit";
import { kickerHtml, sheetHeadHtml } from "./components";
import { paintedIconHtml } from "./featureIcons";
import { showDialog } from "./dialog";
import { toast } from "./toast";
import { showGraphics } from "./graphicsSheet";
import { GraphicsPrefs } from "../render/graphicsPrefs";
import { QUALITY_TIERS } from "../render/quality";
import { guide } from "./guide/guide";
import { SettingsRow, features } from "../features/registry";

/**
 * Player settings (P1.7.4), kept in localStorage; read synchronously by presentation code. The three levels are 0..1:
 * `volume` is the master, `music` and `sfx` the two buses under it (audio.ts). `speed` is the game speed last chosen in a
 * level (the HUD's ×1/×2/×3 button, not a row on this sheet): every level starts at it. A pause is not a speed and is
 * never stored. `haptics` is the player's switch for the platform's haptics (infra/platform/haptics.ts); `leftHanded`
 * moves the map's thumb cluster to the left (hud.css `body.left-handed`); `largeText` steps the type scale up
 * (base.css `body.large-text`). `waveChimes` (M38) plays the soft tick and the milestone call as a wave ends
 * (render/effects.ts, audio/cueRules.ts waveCue). English is the only language in v1.0 (FINISH_PLAN D12), so there is no language field.
 */
export interface SettingsData {
  audio: boolean; volume: number; music: number; sfx: number; waveChimes: boolean;
  haptics: boolean; leftHanded: boolean; largeText: boolean;
  reducedVfx: boolean; colourBlind: boolean; perfHud: boolean; speed: number;
}

const KEY = "highmarch.settings";
/**
 * The stored entry's format, saved alongside the fields as `v`. 2: the Performance overlay is off unless switched on
 * since. The 09-21 builds stored `perfHud: true` as the default, so an entry without `v` has it cleared once.
 */
const SETTINGS_VERSION = 2;
// perfHud is a debug readout: Settings → Performance overlay (or ?stats) turns it on
export const SETTINGS_DEFAULTS: Readonly<SettingsData> = {
  audio: true, volume: 0.7, music: 1, sfx: 1, waveChimes: true,
  haptics: true, leftHanded: false, largeText: false,
  reducedVfx: false, colourBlind: false, perfHud: false, speed: 1,
};

export const Settings = {
  current: load(),
  set(patch: Partial<SettingsData>): void {
    Settings.current = { ...Settings.current, ...patch };
    try { localStorage.setItem(KEY, JSON.stringify({ ...Settings.current, v: SETTINGS_VERSION })); } catch { /* private mode */ }
    apply();
  },
  apply,
};

/**
 * The stored settings over the defaults, field by field: a value of the wrong type falls back to its default and keys
 * that are no longer settings (the old `language`) are dropped, so a stale or hand-edited entry cannot break the sheet.
 */
export function parseSettings(json: string | null): SettingsData {
  let raw: unknown;
  try { raw = JSON.parse(json ?? "{}"); } catch { raw = {}; }
  const saved = raw !== null && typeof raw === "object" ? raw as Record<string, unknown> : {};
  const out: Record<string, unknown> = { ...SETTINGS_DEFAULTS };
  for (const k of Object.keys(SETTINGS_DEFAULTS) as (keyof SettingsData)[]) {
    const v = saved[k];
    if (typeof v !== typeof SETTINGS_DEFAULTS[k]) continue;
    out[k] = typeof v === "number" ? (Number.isFinite(v) ? v : SETTINGS_DEFAULTS[k]) : v;
  }
  const s = out as unknown as SettingsData;
  if (saved.v !== SETTINGS_VERSION) s.perfHud = SETTINGS_DEFAULTS.perfHud;
  for (const k of ["volume", "music", "sfx"] as const) s[k] = Math.max(0, Math.min(1, s[k]));
  return s;
}

function load(): SettingsData {
  try { return parseSettings(localStorage.getItem(KEY)); } catch { return { ...SETTINGS_DEFAULTS }; }
}

/** Body classes let CSS and views react without polling. */
function apply(): void {
  const c = document.body.classList, s = Settings.current;
  c.toggle("reduced-vfx", s.reducedVfx);
  c.toggle("colour-blind", s.colourBlind);
  c.toggle("left-handed", s.leftHanded);
  c.toggle("large-text", s.largeText);
  for (const l of listeners) l();
}

const listeners: (() => void)[] = [];
/** Presentation ports (audio) subscribe to setting changes here: `l` runs after every change; returns the unsubscribe. */
export function onSettingsChange(l: () => void): () => void {
  listeners.push(l);
  return () => { const i = listeners.indexOf(l); if (i >= 0) listeners.splice(i, 1); };
}

/**
 * Whether this device can give haptics at all (the platform feature says so at boot, from `Platform.supportsHaptics`):
 * without them the Haptics row is not shown rather than offered as a switch that does nothing.
 */
let hapticsAvailable = false;
export function setHapticsAvailable(on: boolean): void { hapticsAvailable = on; }

/**
 * The OS reduced-motion query, created once: `matches` on it is live. Calling matchMedia() made and evaluated a new
 * MediaQueryList on every call, and reducedVfx() is asked every sim tick and for every projectile every frame.
 */
const reducedMotion: MediaQueryList | null = typeof matchMedia === "function" ? matchMedia("(prefers-reduced-motion: reduce)") : null;

/** Reduced-motion is honoured as if the reduced-VFX setting were on. */
export function reducedVfx(): boolean {
  return Settings.current.reducedVfx || (reducedMotion?.matches ?? false);
}

/** `onGuide`: the "Guide ›" row (More → Guide), from the hub and the title only; a level shows just the tips toggle. */
export interface SettingsSheetActions { resetSave: () => void; quitLevel?: () => void; playerId?: string; backup?: SaveBackup; onGuide?: () => void }

/**
 * Settings → Save backup (outside a level): the save as JSON text, out through the share sheet or the clipboard and
 * back in through a paste box. `importSave` validates it (the save's own migrate + validate path) and returns an error
 * to show, or takes over (it stores the save and reloads).
 */
export interface SaveBackup {
  exportSave(): string;
  importSave(json: string): string | null;
  share(text: string): Promise<"shared" | "copied" | "failed">;
}

/** The painted cog in the Settings header's portrait tile (the header's art size, components.css .k-head). */
const SETTINGS_PORTRAIT_PX = 52;

/** The three level sliders: which setting each writes and what VoiceOver calls it. */
const SLIDERS: readonly { key: "volume" | "music" | "sfx"; title: string }[] = [
  { key: "volume", title: "Volume" }, { key: "music", title: "Music" }, { key: "sfx", title: "Effects" },
];

/**
 * The settings sheet (kit additions, mockup 11): a title and close button over a scrolling body of `.srow`s in four
 * groups (Sound, Controls, Accessibility, Game), then Quit level (in a level) and Reset save behind `.dialog` confirms,
 * and the player id. Every control is at least a 44 px target (settings.css) and names itself to VoiceOver (the
 * row's title). Rows the features registered (features/registry.ts) close the Game group. With the guide on: the
 * "Captain's tips" toggle (it lives in the save, guide().tipsOff, not in SettingsData).
 */
export function showSettings(parent: HTMLElement, actions: SettingsSheetActions): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = "sheet panel settings";
  // UI pass 10-21: a row is its name and its control; a caption only where it carries a value (the Graphics preset)
  const lbl = (title: string, sub = "") => `<div class="lbl"><b>${title}</b>${sub ? `<small>${sub}</small>` : ""}</div>`;
  const row = (key: keyof SettingsData | "", title: string, sub: string, control: string) => `<div class="srow" ${key ? `data-key="${key}"` : ""}>${lbl(title, sub)}${control}</div>`;
  /** A group: its kicker, then its rows on one cream card (nothing when every row is hidden). */
  const card = (name: string, rows: readonly string[]) => { const r = rows.filter(Boolean); return r.length ? `${group(name)}<div class="scard">${r.join("")}</div>` : ""; };
  const toggle = (on: boolean, name: string) => `<button class="toggle ${on ? "on" : ""}" role="switch" aria-checked="${on}" aria-label="${name}"><span class="knob"></span></button>`;
  const onOff = (key: keyof SettingsData, title: string) => row(key, title, "", toggle(Settings.current[key] === true, title));
  const slider = (v: number, name: string) => {
    const pct = Math.round(v * 100);
    return `<div class="slider" role="slider" tabindex="0" aria-label="${name}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}"><div class="track"><div class="fillx" style="width:${pct}%"></div><div class="thumb" style="left:${pct}%"></div></div></div>`;
  };
  const chev = (cls: string, label: string) => `<button class="btn dark ${cls}"><span>${label}</span>${icon("chev", 16)}</button>`;
  const group = (name: string) => kickerHtml(name);
  // Rows the features registered (features/registry.ts): after the core rows, before the buttons.
  const extra: readonly SettingsRow[] = features.settingsRows(actions.quitLevel !== undefined);
  const render = () => {
    const s = Settings.current, g = guide();
    const scroll = sheet.querySelector<HTMLElement>(".sbody")?.scrollTop ?? 0;
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Settings", portrait: paintedIconHtml("settings", SETTINGS_PORTRAIT_PX) })}
      <div class="sbody">
      ${card("Sound", [
        onOff("audio", "Audio"),
        ...SLIDERS.map((d) => row(d.key, d.title, "", slider(s[d.key], `${d.title} level`))),
        onOff("waveChimes", "Wave chimes"),
      ])}
      ${card("Controls", [
        hapticsAvailable ? onOff("haptics", "Haptics") : "",
        onOff("leftHanded", "Left-handed"),
        g ? `<div class="srow guidetips">${lbl("Captain's tips")}${toggle(!g.tipsOff, "Captain's tips")}</div>` : "",
      ])}
      ${card("Accessibility", [
        onOff("largeText", "Larger text"),
        onOff("colourBlind", "Colour-blind palette"),
        onOff("reducedVfx", "Reduced effects"),
      ])}
      ${card("Game", [
        g && actions.onGuide ? row("", "Guide", "", chev("guiderow", "Open")) : "",
        row("", "Graphics", `${QUALITY_TIERS[GraphicsPrefs.presetIndex()].name}${GraphicsPrefs.customised ? ", customised" : ""}`, chev("gfx", "Adjust")),
        onOff("perfHud", "Performance overlay"),
        actions.backup ? row("", "Back up progress", "", `<span class="bkbtns"><button class="btn dark bk-export"><span>Export</span></button><button class="btn dark bk-import"><span>Import</span></button></span>`) : "",
        ...extra.map((r) => `<div class="srow" data-row="${r.id}">${lbl(r.title())}${r.control()}</div>`),
      ])}
      <div class="row ${actions.quitLevel ? "two" : "one"}">${actions.quitLevel ? btnHtml("QUIT BATTLE", "dark", { className: "quit disp" }) : ""}${btnHtml("START OVER", "dark", { className: "reset disp" })}</div>
      ${actions.playerId ? `<small class="pid">Commander ${actions.playerId}</small>` : ""}
      <small class="pid credits">Music played on the Sonatina Symphonic Orchestra (CC-BY 3.0)</small>
      </div>`;
    const body = sheet.querySelector<HTMLElement>(".sbody")!;
    body.scrollTop = scroll;                                     // a toggle re-renders the sheet: keep the player's place
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    for (const r of extra) { const el = sheet.querySelector<HTMLElement>(`.srow[data-row="${r.id}"]`); if (el) r.bind(el, { parent, rerender: render, close: () => sheet.remove() }); }
    sheet.querySelector<HTMLButtonElement>(".gfx")!.onclick = () => { const g = showGraphics(parent); sheet.style.visibility = "hidden"; new MutationObserver((_, o) => { if (!g.isConnected) { o.disconnect(); sheet.style.visibility = ""; render(); } }).observe(parent, { childList: true }); };
    for (const r of sheet.querySelectorAll<HTMLElement>(".srow[data-key] .toggle")) {
      r.onclick = () => { const k = r.closest<HTMLElement>(".srow")!.dataset.key as keyof SettingsData; Settings.set({ [k]: !Settings.current[k] } as Partial<SettingsData>); render(); };
    }
    sheet.querySelector<HTMLButtonElement>(".guidetips .toggle")?.addEventListener("click", () => { g?.setTipsOff(!g.tipsOff); render(); });
    sheet.querySelector<HTMLButtonElement>(".guiderow")?.addEventListener("click", () => { sheet.remove(); actions.onGuide?.(); });
    for (const d of SLIDERS) bindSlider(sheet.querySelector<HTMLElement>(`.srow[data-key="${d.key}"] .slider`)!, s[d.key], (v) => Settings.set({ [d.key]: v }));
    sheet.querySelector<HTMLButtonElement>(".quit")?.addEventListener("click", async () => {
      if (await showDialog(parent, { title: "QUIT BATTLE?", body: "This battle is lost. Win it another day to claim its chest.", confirm: "QUIT", danger: true })) { sheet.remove(); actions.quitLevel?.(); }
    });
    const backup = actions.backup;
    if (backup) {
      sheet.querySelector<HTMLButtonElement>(".bk-export")!.onclick = async () => {
        const text = backup.exportSave();
        const how = await backup.share(text);
        if (how === "copied") toast(parent, "Progress copied: keep it somewhere safe");
        else if (how === "failed") showBackupSheet(parent, { mode: "export", text });
      };
      sheet.querySelector<HTMLButtonElement>(".bk-import")!.onclick = () => showBackupSheet(parent, { mode: "import", importSave: backup.importSave });
    }
    sheet.querySelector<HTMLButtonElement>(".reset")!.onclick = async () => {
      if (await showDialog(parent, { title: "START OVER?", body: "Every level, tower, chest and piece of gear is wiped. There's no coming back from this.", confirm: "WIPE IT", danger: true })) actions.resetSave();
    };
  };
  render();
  parent.append(sheet);
  return sheet;
}

/** A keyboard (or switch-control) step on a level slider: a tenth of the range. */
const SLIDER_STEP = 0.1;

/**
 * Drag or tap anywhere on the track; writes 0..1 on release and while dragging. The arrow keys, Home and End step it
 * for keyboard and switch-control players, and `aria-valuenow` follows so a screen reader reads the new level.
 */
function bindSlider(el: HTMLElement, value: number, onChange: (v: number) => void): void {
  const track = el.querySelector<HTMLElement>(".track")!;
  const fill = el.querySelector<HTMLElement>(".fillx")!;
  const thumb = el.querySelector<HTMLElement>(".thumb")!;
  let v = value;
  const show = () => {
    const pct = Math.round(v * 100);
    fill.style.width = thumb.style.left = `${pct}%`;
    el.setAttribute("aria-valuenow", String(pct));
  };
  const setFrom = (clientX: number) => {
    const r = track.getBoundingClientRect();
    v = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    show();
  };
  el.onpointerdown = (e) => { el.setPointerCapture(e.pointerId); setFrom(e.clientX); };
  el.onpointermove = (e) => { if (el.hasPointerCapture(e.pointerId)) setFrom(e.clientX); };
  el.onpointerup = (e) => { if (el.hasPointerCapture(e.pointerId)) { el.releasePointerCapture(e.pointerId); setFrom(e.clientX); onChange(v); } };
  el.onkeydown = (e) => {
    const step = { ArrowRight: SLIDER_STEP, ArrowUp: SLIDER_STEP, ArrowLeft: -SLIDER_STEP, ArrowDown: -SLIDER_STEP, Home: -1, End: 1 }[e.key];
    if (step === undefined) return;
    e.preventDefault();
    v = Math.max(0, Math.min(1, Math.round((v + step) * 100) / 100));
    show();
    onChange(v);
  };
}

/**
 * The backup text box: export shows the save selected for a manual copy (when neither the share sheet nor the
 * clipboard took it); import takes a pasted save, confirms the overwrite and hands it to `importSave`.
 */
function showBackupSheet(parent: HTMLElement, o: { mode: "export"; text: string } | { mode: "import"; importSave: (json: string) => string | null }): void {
  const scrim = document.createElement("div");
  scrim.className = "scrim backupscrim";
  const exporting = o.mode === "export";
  scrim.innerHTML = `<div class="panel dialog backup">${ribbonHtml(exporting ? "BACK UP PROGRESS" : "RESTORE PROGRESS", "", "dtitle disp")}
    <p>${exporting ? "Copy this text and keep it somewhere safe." : "Paste a backup. It takes the place of your current progress."}</p>
    <textarea spellcheck="false" autocapitalize="off" autocomplete="off" ${exporting ? "readonly" : `placeholder="Paste your backup here"`}></textarea>
    <p class="err hidden"></p>
    <div class="row two">${btnHtml(exporting ? "DONE" : "CANCEL", "dark", { className: "cancel disp" })}${exporting ? btnHtml("SELECT ALL", "blue", { className: "sel disp" }) : btnHtml("IMPORT", "danger", { className: "ok disp accent" })}</div></div>`;
  const area = scrim.querySelector<HTMLTextAreaElement>("textarea")!;
  const err = scrim.querySelector<HTMLElement>(".err")!;
  const close = () => scrim.remove();
  scrim.querySelector<HTMLButtonElement>(".cancel")!.onclick = close;
  scrim.onclick = (e) => { if (e.target === scrim) close(); };
  if (o.mode === "export") {
    area.value = o.text;
    scrim.querySelector<HTMLButtonElement>(".sel")!.onclick = () => { area.focus(); area.select(); area.setSelectionRange(0, area.value.length); };
  } else {
    const importSave = o.importSave;
    scrim.querySelector<HTMLButtonElement>(".ok")!.onclick = async () => {
      const text = area.value.trim();
      if (!text) return;
      if (!(await showDialog(parent, { title: "REPLACE PROGRESS?", body: "Your current campaign gives way to the backup. There's no undo.", confirm: "REPLACE", danger: true }))) return;
      const problem = importSave(text);
      if (problem) { err.textContent = problem; err.classList.remove("hidden"); }
    };
  }
  parent.append(scrim);
}
